import { useRef, useMemo } from "react";
import { Bar } from "react-chartjs-2";
import { AssetInsight } from "@/api/studio/insights/types/insight.type";
import { ChartOptions } from "chart.js";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartData } from "chart.js";
import { CHART_CONSTANTS } from "../consts/report-chart-constants";
import { useGlobalStore } from "@/stores/global-store";
import { getThumbnailImage } from "../utils/get-thumbnail-image";

/* Register ChartJS components required for the bar chart */
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

/* Dataset Type */
export interface ChartDataset {
  label: string;
  colors: {
    border: string;
    background: string;
  };
  getData: (insight: AssetInsight) => number;
}

/* Prop Types */
interface ReportBarChartProps {
  insightData: AssetInsight[];
  datasets?: ChartDataset[];
  variant?: "attention" | "visibility" | "clarity" | "digestibility" | "creative" | "emotion" | "memory" | "custom";
  studyId: string;
  projectId: string;
}

/*
 * Chart options configuration
 * Defines the appearance and behavior of the bar chart
 */
const options: ChartOptions<"bar"> = {
  indexAxis: "x",
  elements: {
    bar: {
      borderWidth: 2,
    },
  },
  responsive: true,
  maintainAspectRatio: false,
  layout: {
    padding: {
      bottom: CHART_CONSTANTS.IMAGE_SIZE + CHART_CONSTANTS.TEXT_HEIGHT + CHART_CONSTANTS.PADDING,
    },
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { display: false },
    },
    y: {
      grid: { display: false },
      beginAtZero: true,
      max: 100,
      ticks: {
        stepSize: 10,
        callback: (value) => `${value}%`,
      },
    },
  },
  plugins: {
    title: { display: false },
    legend: { display: false },
  },
};

/*
 * ReportBarChart Component
 * Displays metrics for insights in a bar chart format with thumbnails
 * Used in the reports pages to visualize ad performance
 */
export const ReportBarChart = ({ insightData, datasets, variant = "attention", studyId, projectId }: ReportBarChartProps) => {
  /* Refs for managing chart and image state */
  const chartRef = useRef<ChartJS<"bar">>(null);
  const imagesRef = useRef<Map<string, HTMLImageElement>>(new Map());
  const loadingRef = useRef<boolean>(false);

  const theme = useGlobalStore((state) => state.theme);

  /* Default datasets based on variant */
  const defaultDatasets = useMemo(() => {
    /* Base datasets for different variants */
    const variantDatasets: Record<string, ChartDataset[]> = {
      attention: [
        {
          label: "Attention",
          colors: {
            border: "rgb(242, 49, 138)",
            background: "rgb(242, 49, 138)",
          },
          getData: (insight) => insight.metrics.attention.score,
        },
        {
          label: "Clarity",
          colors: {
            border: "rgb(25, 211, 200)",
            background: "rgb(25, 211, 200)",
          },
          getData: (insight) => insight.metrics.attention.clarity.score,
        },
        {
          label: "Digestibility",
          colors: {
            border: "rgb(242, 153, 74)",
            background: "rgb(242, 153, 74)",
          },
          getData: (insight) => insight.metrics.attention.digestibility.score,
        },
      ],
      visibility: [
        {
          label: "Visibility",
          colors: {
            border: "rgb(242, 49, 138)",
            background: "rgb(242, 49, 138)",
          },
          getData: (insight) => insight.metrics.attention.visibility?.score ?? 0,
        },
      ],
      clarity: [
        {
          label: "Clarity",
          colors: {
            border: "rgb(242, 49, 138)",
            background: "rgb(242, 49, 138)",
          },

          getData: (insight) => insight.metrics.attention.clarity.score,
        },
        {
          label: "Hotspots prominence",
          colors: {
            border: "rgb(166, 107, 243)",
            background: "rgb(166, 107, 243)",
          },
          getData: (insight) => insight.metrics.attention.clarity.prominence,
        },
      ],
      digestibility: [
        {
          label: "Digestibility",
          colors: {
            border: "rgb(242, 49, 138)",
            background: "rgb(242, 49, 138)",
          },
          getData: (insight) => insight.metrics.attention.digestibility.score,
        },
        {
          label: "AOI Prominence",
          colors: {
            border: "rgb(166, 107, 243)",
            background: "rgb(166, 107, 243)",
          },
          getData: (insight) => insight.metrics.attention.digestibility.prominence,
        },
      ],
      creative: [
        {
          label: "Creative",
          colors: {
            border: "rgb(242, 49, 138)",
            background: "rgb(242, 49, 138)",
          },
          getData: (insight) => Math.round(insight.metrics.creative?.score ?? 0),
        },
        {
          label: "Attention",
          colors: {
            border: "rgb(25, 211, 200)",
            background: "rgb(25, 211, 200)",
          },
          getData: (insight) => insight.metrics.attention.score,
        },
        {
          label: "Emotion",
          colors: {
            border: "rgb(242, 153, 74)",
            background: "rgb(242, 153, 74)",
          },
          getData: (insight) => Math.round(insight.metrics.emotion?.score ?? 0),
        },
        {
          label: "Memory",
          colors: {
            border: "rgb(166, 107, 243)",
            background: "rgb(166, 107, 243)",
          },
          getData: (insight) => Math.round(insight.metrics.memory?.score ?? 0),
        },
      ],
      emotion: [
        {
          label: "Emotion",
          colors: {
            border: "rgb(242, 49, 138)",
            background: "rgb(242, 49, 138)",
          },
          getData: (insight) => Math.round(insight.metrics.emotion?.score ?? 0),
        },
      ],
      memory: [
        {
          label: "Memory",
          colors: {
            border: "rgb(242, 49, 138)",
            background: "rgb(242, 49, 138)",
          },
          getData: (insight) => Math.round(insight.metrics.memory?.score ?? 0),
        },
      ],
    };

    /* Get datasets for the selected variant */
    const selectedDatasets = variantDatasets[variant] || variantDatasets.attention;

    /* Add visibility dataset if it exists in the data - for attention variant */
    if (variant === "attention" && insightData.some((insight) => "visibility" in insight.metrics.attention)) {
      // Create visibility dataset
      const visibilityDataset = {
        label: "Visibility",
        colors: {
          border: "rgb(78, 73, 156)",
          background: "rgb(78, 73, 156)",
        },
        getData: (insight: AssetInsight) => insight.metrics.attention.visibility?.score ?? 0,
      };

      /* Insert visibility right after attention (at index 1) */
      selectedDatasets.splice(1, 0, visibilityDataset);
    }

    return selectedDatasets;
  }, [variant, insightData]);

  /* Use provided datasets or fall back to default ones */
  const chartDatasets = useMemo(() => {
    return datasets || defaultDatasets;
  }, [datasets, defaultDatasets]);

  /*
   * Loads and caches an image for an insight
   * Returns cached image if available, otherwise creates and caches a new one
   */
  const loadImage = (insight: AssetInsight): HTMLImageElement => {
    const cachedImage = imagesRef.current.get(insight.key);
    if (cachedImage?.complete) return cachedImage;

    const img = new Image();
    img.src = getThumbnailImage(insight.key, 300, 300, projectId, studyId);

    img.onload = () => {
      if (chartRef.current) {
        chartRef.current.update();
      }
    };

    imagesRef.current.set(insight.key, img);
    return img;
  };

  /*
   * Prepare chart data from insights
   * Maps insight metrics to dataset format required by Chart.js
   */
  const data: ChartData<"bar"> = {
    labels: insightData.map((_, index) => `Ad ${index + 1}`),
    datasets: [
      ...chartDatasets.map((dataset, index) => ({
        label: dataset.label,
        data: insightData.map(dataset.getData),
        borderColor: dataset.colors.border,
        backgroundColor: dataset.colors.background,
        borderWidth: 1,
        borderRadius: 6,
        stack: `stack${index}`,
        order: 1, // Draw in front of background
      })),
    ],
  };

  /*
   * Plugin hook to initiate image loading before drawing
   * Ensures images are loaded or loading before chart render
   */
  const beforeDrawImages = () => {
    if (loadingRef.current) return;
    loadingRef.current = true;

    try {
      insightData.forEach(loadImage);
    } finally {
      loadingRef.current = false;
    }
  };

  /*
   * Plugin hook to draw images and labels after datasets
   * Handles image placement, background drawing, and text labels
   */
  const drawImagesAndLabels = (chart: ChartJS) => {
    const ctx = chart.ctx;
    const xAxis = chart.scales.x;
    const yAxis = chart.scales.y;

    /* Setup text rendering context */
    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.font = CHART_CONSTANTS.FONT;
    ctx.fillStyle = CHART_CONSTANTS.TEXT_COLOR;

    xAxis.ticks.forEach((_, index) => {
      const insight = insightData[index];
      const img = imagesRef.current.get(insight.key);
      const x = xAxis.getPixelForTick(index);

      if (img?.complete) {
        const imageX = x - CHART_CONSTANTS.SQUARE_SIZE / 2;
        const imageY = yAxis.bottom + CHART_CONSTANTS.PADDING;

        ctx.save();

        /* Enable high-quality image rendering */
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";

        /* Set background color based on theme */
        const backgroundColor = theme === "light" ? "rgba(255, 255, 255, 1)" : "#28282c";

        /* Draw and clip container background */
        ctx.beginPath();
        ctx.fillStyle = backgroundColor;
        ctx.roundRect(imageX, imageY, CHART_CONSTANTS.SQUARE_SIZE, CHART_CONSTANTS.SQUARE_SIZE, CHART_CONSTANTS.BORDER_RADIUS);
        ctx.fill();

        ctx.beginPath();
        ctx.roundRect(imageX, imageY, CHART_CONSTANTS.SQUARE_SIZE, CHART_CONSTANTS.SQUARE_SIZE, CHART_CONSTANTS.BORDER_RADIUS);
        ctx.clip();

        /* Calculate image dimensions maintaining aspect ratio */
        const aspectRatio = img.width / img.height;
        let drawWidth = CHART_CONSTANTS.SQUARE_SIZE;
        let drawHeight = CHART_CONSTANTS.SQUARE_SIZE;

        if (aspectRatio > 1) {
          /* Handle landscape images */
          drawHeight = CHART_CONSTANTS.SQUARE_SIZE / aspectRatio;
          const verticalOffset = (CHART_CONSTANTS.SQUARE_SIZE - drawHeight) / 2;
          ctx.drawImage(img, imageX, imageY + verticalOffset, drawWidth, drawHeight);
        } else {
          /* Handle portrait images */
          drawWidth = CHART_CONSTANTS.SQUARE_SIZE * aspectRatio;
          const horizontalOffset = (CHART_CONSTANTS.SQUARE_SIZE - drawWidth) / 2;
          ctx.drawImage(img, imageX + horizontalOffset, imageY, drawWidth, drawHeight);
        }

        ctx.restore();

        /* Draw label below image */
        // const textY = imageY + CHART_CONSTANTS.SQUARE_SIZE + 5;
        // ctx.fillText(insight.assetName, x, textY);
      }
    });

    ctx.restore();
  };

  // Create chart options with access to insightData
  const chartOptions: ChartOptions<"bar"> = useMemo(() => {
    return {
      ...options,
      plugins: {
        ...options.plugins,
        tooltip: {
          enabled: true,
          callbacks: {
            title: (context) => {
              const index = context[0].dataIndex;
              return insightData[index]?.assetName || `Ad ${index + 1}`;
            },
            label: (context) => {
              return `${context.dataset.label}: ${context.parsed.y}%`;
            },
          },
        },
      },
    };
  }, [insightData]);

  return (
    <Bar
      ref={chartRef}
      options={chartOptions}
      data={data}
      plugins={[
        {
          id: "chartImages",
          beforeDatasetsDraw: beforeDrawImages,
          afterDatasetsDraw: drawImagesAndLabels,
        },
      ]}
    />
  );
};
